import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { JsonLd } from '../components/JsonLd';
import { usePageMeta } from '../hooks/usePageMeta';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { useI18nLocaleSync } from '../hooks/useI18nLocaleSync';
import { useAppLocale } from '../hooks/useAppLocale';
import { pathWithLocale } from '../lib/locale';

type FaqPair = { q: string; a: string };

function buildFaqLd(items: FaqPair[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: a.replace(/<[^>]+>/g, ' ').replace(/\{\{[^}]+\}\}/g, '').replace(/\s+/g, ' ').trim(),
      },
    })),
  };
}

function FaqAnswerHtml({ html, simulateHref, autoHref }: { html: string; simulateHref: string; autoHref: string }) {
  const fixed = html.replace(/\{\{simulate\}\}/g, simulateHref).replace(/\{\{auto\}\}/g, autoHref);
  return <p dangerouslySetInnerHTML={{ __html: fixed }} />;
}

export function Faq() {
  const { t } = useTranslation();
  const locale = useAppLocale();
  const lp = useLocalizedPath();

  useI18nLocaleSync();

  usePageMeta({
    title: t('faq.metaTitle'),
    description: t('faq.metaDesc'),
    path: pathWithLocale('/faq', locale),
  });

  const sectionGen = t('faq.sectionGen');
  const sectionSync = t('faq.sectionSync');

  const gen = t('faq.itemsGen', { returnObjects: true }) as FaqPair[];
  const sync = t('faq.itemsSync', { returnObjects: true }) as FaqPair[];

  const ldData = useMemo(() => buildFaqLd([...gen, ...sync]), [gen, sync]);

  const simulateHref = lp('/simulate');
  const autoHref = `${lp('/')}#auto-search`;

  return (
    <main className="content">
      <JsonLd id={`ld-faq-${locale}`} data={ldData} />
      <nav className="breadcrumb" aria-label={t('layout.breadcrumbLabel')}>
        <Link to={lp('/')}>{t('breadcrumb.home')}</Link>
        <span className="sep">/</span>
        <span className="current">{t('nav.faq')}</span>
      </nav>

      <article className="about-section">
        <h1>{t('faq.title')}</h1>
        <p className="story-qa-intro">
          <Trans
            i18nKey="faq.intro"
            components={{
              storyLink: <Link to={lp('/story')} />,
              about: <Link to={lp('/about')} />,
            }}
          />
        </p>

        <h2 className="faq-section-heading" style={{ fontSize: '1.12em', marginTop: 24, marginBottom: 12 }}>
          {sectionGen}
        </h2>

        {gen.map((item, i) => (
          <div className="faq-item" key={`g-${i}`}>
            <h2>{item.q}</h2>
            <FaqAnswerHtml html={item.a} simulateHref={simulateHref} autoHref={autoHref} />
          </div>
        ))}

        <h2 className="faq-section-heading" style={{ fontSize: '1.12em', marginTop: 28, marginBottom: 12 }}>
          {sectionSync}
        </h2>

        {sync.map((item, i) => (
          <div className="faq-item" key={`s-${i}`}>
            <h2>{item.q}</h2>
            <FaqAnswerHtml html={item.a} simulateHref={simulateHref} autoHref={autoHref} />
          </div>
        ))}
      </article>
    </main>
  );
}
